<template>
  <div class="privacy-page">
    <div class="container py-5">
      <div class="row justify-content-center">
        <div class="col-lg-10 col-xl-9">
          <!-- Header -->
          <div class="page-header mb-5 fade-in-up">
            <h1 class="display-4 fw-bold mb-3">{{ $t('privacy.title') }}</h1>
            <p class="text-muted mb-2">
              <strong>{{ $t('privacy.lastUpdated') }}:</strong> {{ $t('privacy.effectiveDate') }}
            </p>
          </div>
          
          <div class="content-section fade-in">
            <div class="card shadow-sm border-0 mb-4">
              <div class="card-body p-4 p-md-5">
                <!-- Introduction -->
                <div class="section-intro mb-5">
                  <p class="lead">{{ $t('privacy.intro') }}</p>
                </div>

                <!-- 1. Information Collection -->
                <section class="policy-section mb-5">
                  <h2 class="section-title">一、{{ $t('privacy.sections.infoCollection.title') }}</h2>
                  
                  <div class="subsection mb-4">
                    <h3 class="subsection-title">2.1 {{ $t('privacy.sections.infoCollection.account.title') }}</h3>
                    <p class="subsection-content">{{ $t('privacy.sections.infoCollection.account.content') }}</p>
                    <ul class="info-list">
                      <li v-for="(item, index) in getItems('privacy.sections.infoCollection.account.items')" :key="index">{{ item }}</li>
                    </ul>
                  </div>

                  <div class="subsection mb-4">
                    <h3 class="subsection-title">2.2 {{ $t('privacy.sections.infoCollection.thirdParty.title') }}</h3>
                    <p class="subsection-content">{{ $t('privacy.sections.infoCollection.thirdParty.content') }}</p>
                    <ul class="info-list">
                      <li v-for="(item, index) in getItems('privacy.sections.infoCollection.thirdParty.items')" :key="index">{{ item }}</li>
                    </ul>
                  </div>

                  <div class="subsection mb-4">
                    <h3 class="subsection-title">2.3 {{ $t('privacy.sections.infoCollection.nfc.title') }}</h3>
                    <p class="subsection-content">{{ $t('privacy.sections.infoCollection.nfc.content') }}</p>
                    <ul class="info-list">
                      <li v-for="(item, index) in getItems('privacy.sections.infoCollection.nfc.items')" :key="index">{{ item }}</li>
                    </ul>
                  </div>

                  <div class="subsection mb-4">
                    <h3 class="subsection-title">2.4 {{ $t('privacy.sections.infoCollection.files.title') }}</h3>
                    <p class="subsection-content">{{ $t('privacy.sections.infoCollection.files.content') }}</p>
                    <ul class="info-list">
                      <li v-for="(item, index) in getItems('privacy.sections.infoCollection.files.items')" :key="index">{{ item }}</li>
                    </ul>
                    <p class="note-text"><strong>{{ $t('privacy.sections.infoCollection.files.note') }}</strong></p>
                  </div>

                  <div class="subsection mb-4">
                    <h3 class="subsection-title">2.5 {{ $t('privacy.sections.infoCollection.payment.title') }}</h3>
                    <p class="subsection-content">{{ $t('privacy.sections.infoCollection.payment.content') }}</p>
                    <ul class="info-list">
                      <li v-for="(item, index) in getItems('privacy.sections.infoCollection.payment.items')" :key="index">{{ item }}</li>
                    </ul>
                    <p class="note-text"><strong>{{ $t('privacy.sections.infoCollection.payment.note') }}</strong></p>
                  </div>

                  <div class="subsection mb-4">
                    <h3 class="subsection-title">2.6 {{ $t('privacy.sections.infoCollection.device.title') }}</h3>
                    <p class="subsection-content">{{ $t('privacy.sections.infoCollection.device.content') }}</p>
                    <ul class="info-list">
                      <li v-for="(item, index) in getItems('privacy.sections.infoCollection.device.items')" :key="index">{{ item }}</li>
                    </ul>
                  </div>

                  <div class="subsection mb-4">
                    <h3 class="subsection-title">2.7 {{ $t('privacy.sections.infoCollection.usage.title') }}</h3>
                    <p class="subsection-content">{{ $t('privacy.sections.infoCollection.usage.content') }}</p>
                    <ul class="info-list">
                      <li v-for="(item, index) in getItems('privacy.sections.infoCollection.usage.items')" :key="index">{{ item }}</li>
                    </ul>
                  </div>
                </section>

                <!-- 2. Information Usage -->
                <section class="policy-section mb-5">
                  <h2 class="section-title">二、{{ $t('privacy.sections.infoUsage.title') }}</h2>
                  <p class="subsection-content">{{ $t('privacy.sections.infoUsage.content') }}</p>
                  <ol class="info-list" v-if="getItems('privacy.sections.infoUsage.items').length > 0">
                    <li v-for="(item, index) in getItems('privacy.sections.infoUsage.items')" :key="index">{{ item }}</li>
                  </ol>
                  <p v-else class="text-danger">⚠️ 列表数据加载失败</p>
                </section>

                <!-- 3. Information Storage -->
                <section class="policy-section mb-5">
                  <h2 class="section-title">三、{{ $t('privacy.sections.infoStorage.title') }}</h2>
                  
                  <div class="subsection mb-4">
                    <h3 class="subsection-title">4.1 {{ $t('privacy.sections.infoStorage.local.title') }}</h3>
                    <p class="subsection-content">{{ $t('privacy.sections.infoStorage.local.content') }}</p>
                    <ul class="info-list">
                      <li v-for="(item, index) in getItems('privacy.sections.infoStorage.local.items')" :key="index">{{ item }}</li>
                    </ul>
                  </div>

                  <div class="subsection mb-4">
                    <h3 class="subsection-title">4.2 {{ $t('privacy.sections.infoStorage.server.title') }}</h3>
                    <p class="subsection-content">{{ $t('privacy.sections.infoStorage.server.content') }}</p>
                    <ul class="info-list">
                      <li v-for="(item, index) in getItems('privacy.sections.infoStorage.server.items')" :key="index">{{ item }}</li>
                    </ul>
                  </div>

                  <div class="subsection mb-4">
                    <h3 class="subsection-title">4.3 {{ $t('privacy.sections.infoStorage.security.title') }}</h3>
                    <p class="subsection-content">{{ $t('privacy.sections.infoStorage.security.content') }}</p>
                    <ul class="info-list">
                      <li v-for="(item, index) in getItems('privacy.sections.infoStorage.security.items')" :key="index">{{ item }}</li>
                    </ul>
                  </div>
                </section>

                <!-- 4. Third Party Services -->
                <section class="policy-section mb-5">
                  <h2 class="section-title">四、{{ $t('privacy.sections.thirdPartyServices.title') }}</h2>
                  <div v-for="(service, index) in getItems('privacy.sections.thirdPartyServices.items')" :key="index" class="service-card mb-3">
                    <h4 class="service-name">{{ index + 1 }}. {{ service.name }}</h4>
                    <p class="mb-1"><strong>用途/Purpose:</strong> {{ service.purpose }}</p>
                    <p class="mb-1"><strong>数据范围/Data:</strong> {{ service.data }}</p>
                    <p class="mb-0"><strong>隐私政策/Policy:</strong> <a :href="service.policy" target="_blank" rel="noopener noreferrer" class="text-primary">{{ service.policy }}</a></p>
                  </div>
                </section>

                <!-- 5. Information Sharing -->
                <section class="policy-section mb-5">
                  <h2 class="section-title">五、{{ $t('privacy.sections.infoSharing.title') }}</h2>
                  
                  <div class="subsection mb-4">
                    <h3 class="subsection-title">6.1 {{ $t('privacy.sections.infoSharing.noSale') }}</h3>
                    <p class="subsection-content">{{ $t('privacy.sections.infoSharing.noSaleContent') }}</p>
                  </div>

                  <div class="subsection mb-4">
                    <h3 class="subsection-title">6.2 {{ $t('privacy.sections.infoSharing.shareTitle') }}</h3>
                    <ol class="info-list">
                      <li v-for="(item, index) in getItems('privacy.sections.infoSharing.shareItems')" :key="index">{{ item }}</li>
                    </ol>
                  </div>
                </section>

                <!-- 6. User Rights -->
                <section class="policy-section mb-5">
                  <h2 class="section-title">六、{{ $t('privacy.sections.userRights.title') }}</h2>
                  <p class="subsection-content">{{ $t('privacy.sections.userRights.content') }}</p>
                  <ol class="info-list mb-4">
                    <li v-for="(item, index) in getItems('privacy.sections.userRights.items')" :key="index">{{ item }}</li>
                  </ol>
                  <p class="subsection-content mb-2">{{ $t('privacy.sections.userRights.contact') }}</p>
                  <ul class="info-list">
                    <li v-for="(method, index) in getItems('privacy.sections.userRights.contactMethods')" :key="index">{{ method }}</li>
                  </ul>
                </section>

                <!-- 7. Children Privacy -->
                <section class="policy-section mb-5">
                  <h2 class="section-title">七、{{ $t('privacy.sections.childrenPrivacy.title') }}</h2>
                  <p class="subsection-content">{{ $t('privacy.sections.childrenPrivacy.content') }}</p>
                </section>

                <!-- 8. Data Retention -->
                <section class="policy-section mb-5">
                  <h2 class="section-title">八、{{ $t('privacy.sections.dataRetention.title') }}</h2>
                  <p class="subsection-content">{{ $t('privacy.sections.dataRetention.content') }}</p>
                  <ul class="info-list">
                    <li v-for="(item, index) in getItems('privacy.sections.dataRetention.items')" :key="index">{{ item }}</li>
                  </ul>
                </section>

                <!-- 9. International Transfer -->
                <section class="policy-section mb-5">
                  <h2 class="section-title">九、{{ $t('privacy.sections.international.title') }}</h2>
                  <p class="subsection-content">{{ $t('privacy.sections.international.content') }}</p>
                </section>

                <!-- 10. Policy Update -->
                <section class="policy-section mb-5">
                  <h2 class="section-title">十、{{ $t('privacy.sections.policyUpdate.title') }}</h2>
                  <p class="subsection-content">{{ $t('privacy.sections.policyUpdate.content') }}</p>
                </section>

                <!-- 11. Contact Us -->
                <section class="policy-section mb-5">
                  <h2 class="section-title">十一、{{ $t('privacy.sections.contactUs.title') }}</h2>
                  <p class="subsection-content mb-3">{{ $t('privacy.sections.contactUs.content') }}</p>
                  <ul class="info-list">
                    <li v-for="(item, index) in getItems('privacy.sections.contactUs.items')" :key="index">{{ item }}</li>
                  </ul>
                </section>

                <!-- 12. Applicable Law -->
                <section class="policy-section mb-5">
                  <h2 class="section-title">十二、{{ $t('privacy.sections.law.title') }}</h2>
                  <p class="subsection-content">{{ $t('privacy.sections.law.content') }}</p>
                </section>

                <!-- 13. Important Notice -->
                <section class="policy-section">
                  <div class="notice-box">
                    <h2 class="section-title mb-3">
                      <i class="bi bi-exclamation-triangle-fill me-2"></i>{{ $t('privacy.sections.notice.title') }}
                    </h2>
                    <p class="subsection-content mb-0">{{ $t('privacy.sections.notice.content') }}</p>
                  </div>
                </section>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'Privacy',
  methods: {
    getItems(key) {
      try {
        // 直接从i18n的messages中获取，避免$t()可能返回字符串的问题
        const locale = this.$i18n.locale
        const messages = this.$i18n.messages[locale]
        if (!messages) {
          console.warn('Messages not found for locale:', locale)
          return []
        }
        
        const keys = key.split('.')
        let value = messages
        for (const k of keys) {
          if (value && typeof value === 'object' && k in value) {
            value = value[k]
          } else {
            console.warn(`Key not found: ${key} at ${k}`, 'Current value:', value)
            return []
          }
        }
        
        // 确保返回的是数组
        if (Array.isArray(value)) {
          return value
        } else {
          console.warn(`Value for ${key} is not an array:`, typeof value, value)
          return []
        }
      } catch (error) {
        console.error('Error getting items for key:', key, error)
        return []
      }
    }
  },
  mounted() {
    // 调试：检查数据是否正确加载
    console.log('Privacy page mounted, locale:', this.$i18n.locale)
    console.log('Info usage items:', this.getItems('privacy.sections.infoUsage.items'))
  }
}
</script>

<style scoped>
.privacy-page {
  padding-top: calc(72px + var(--spacing-xl));
  min-height: calc(100vh - 200px);
  background: var(--color-background);
}

.page-header {
  text-align: center;
  margin-bottom: var(--spacing-xl);
}

.page-header h1 {
  font-size: 48px;
  line-height: 1.08349;
  font-weight: 700;
  letter-spacing: -0.003em;
  margin-bottom: var(--spacing-sm);
  color: var(--color-text-primary);
}

.page-header p {
  font-size: 15px;
  line-height: 1.42859;
  color: var(--color-text-secondary);
}

.content-section {
  animation: fadeIn 0.6s ease-out;
}

.card {
  border-radius: var(--radius-lg);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.04);
  background: var(--color-surface);
}

.section-intro {
  padding: var(--spacing-lg);
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-radius: var(--radius-md);
  border-left: 4px solid var(--color-primary);
}

.section-intro .lead {
  font-size: 19px;
  line-height: 1.5;
  color: var(--color-text-primary);
  margin-bottom: 0;
}

.policy-section {
  margin-bottom: var(--spacing-xl);
}

.section-title {
  font-size: 32px;
  line-height: 1.2;
  font-weight: 700;
  color: var(--color-text-primary);
  margin-bottom: var(--spacing-lg);
  padding-bottom: var(--spacing-sm);
  border-bottom: 3px solid var(--color-primary);
}

.subsection {
  margin-bottom: var(--spacing-lg);
}

.subsection-title {
  font-size: 22px;
  line-height: 1.3;
  font-weight: 600;
  color: var(--color-text-primary);
  margin-bottom: var(--spacing-sm);
}

.subsection-content {
  font-size: 17px;
  line-height: 1.6;
  color: var(--color-text-secondary);
  margin-bottom: var(--spacing-sm);
}

.info-list {
  font-size: 16px;
  line-height: 1.7;
  color: var(--color-text-secondary);
  padding-left: var(--spacing-lg);
  margin-bottom: var(--spacing-md);
}

.info-list li {
  margin-bottom: var(--spacing-xs);
}

.note-text {
  font-size: 15px;
  line-height: 1.6;
  color: var(--color-primary);
  font-style: italic;
  margin-top: var(--spacing-sm);
}

.service-card {
  padding: var(--spacing-md);
  background: #f8f9fa;
  border-radius: var(--radius-md);
  border-left: 3px solid var(--color-primary);
}

.service-name {
  font-size: 19px;
  font-weight: 600;
  color: var(--color-text-primary);
  margin-bottom: var(--spacing-sm);
}

.service-card p {
  font-size: 15px;
  line-height: 1.6;
  color: var(--color-text-secondary);
}

.service-card a {
  word-break: break-all;
  text-decoration: none;
}

.service-card a:hover {
  text-decoration: underline;
}

.notice-box {
  padding: var(--spacing-lg);
  background: linear-gradient(135deg, #fff3cd 0%, #ffeaa7 100%);
  border-radius: var(--radius-md);
  border: 2px solid #ffc107;
}

.notice-box .section-title {
  border-bottom: none;
  color: #856404;
  margin-bottom: var(--spacing-md);
}

.notice-box .subsection-content {
  color: #856404;
  font-weight: 500;
}

.fade-in-up {
  animation: fadeInUp 0.8s ease-out;
}

.fade-in {
  animation: fadeIn 1s ease-out;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@media (max-width: 992px) {
  .page-header h1 {
    font-size: 40px;
  }
  
  .section-title {
    font-size: 28px;
  }
  
  .subsection-title {
    font-size: 20px;
  }
}

@media (max-width: 734px) {
  .privacy-page {
    padding-top: calc(72px + var(--spacing-lg));
  }
  
  .page-header h1 {
    font-size: 32px;
    line-height: 1.125;
  }
  
  .card-body {
    padding: var(--spacing-md) !important;
  }
  
  .section-title {
    font-size: 24px;
  }
  
  .subsection-title {
    font-size: 18px;
  }
  
  .subsection-content,
  .info-list {
    font-size: 15px;
  }
  
  .section-intro .lead {
    font-size: 16px;
  }
}
</style>
